import { useRef, useState, type DragEvent } from 'react'
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import StarIcon from '@mui/icons-material/StarBorder'
import AddPhotoIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import { PHOTO_HEIGHT, PHOTO_WIDTH, refuserFichier, resizeToPortrait } from './image-resize.js'

/** Taille d'AFFICHAGE des vignettes — indépendante de la résolution stockée. */
const VIGNETTE_W = 96
const VIGNETTE_H = 128

/**
 * Plusieurs visuels pour un article (5 au maximum).
 *
 * ⭐ Le PREMIER fait office de vignette : c'est lui qui apparaît dans les
 * listes, la vue cartes et la palette de commandes. L'ordre porte donc une
 * information, d'où le bouton « mettre en premier » plutôt qu'un simple
 * glisser-déposer, plus difficile à atteindre au comptoir sur écran tactile.
 *
 * Le plafond de 5 n'est pas décoratif : chaque image pèse 5 à 17 Ko après
 * réduction (mesuré) et voyage dans la réplique de TOUS les appareils.
 */

const MAX_DEFAUT = 5

export function PhotoGallery({
  values,
  onChange,
  max = MAX_DEFAUT,
  legende,
}: {
  values: string[]
  onChange: (images: string[]) => void
  /**
   * Nombre de visuels admis.
   *
   * ⭐ À 1, le composant sert de sélecteur d'image UNIQUE — le logo d'une
   * boutique, par exemple. La badge « Vignette » et le bouton « mettre en
   * premier » disparaissent alors d'eux-mêmes : classer une image seule n'a
   * pas de sens. C'est la même tuile, le même glisser-déposer, le même
   * redimensionnement — la cohérence vient du composant, pas d'une consigne.
   */
  max?: number
  /** Remplace la mention « n / max » sous les tuiles. */
  legende?: string
}): JSX.Element {
  const MAX = max
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)

  const complet = values.length >= MAX

  async function ajouter(files: FileList | File[]): Promise<void> {
    setError(null)
    const restants = MAX - values.length
    if (restants <= 0) {
      setError(`${MAX} photos au maximum.`)
      return
    }

    const liste = [...files].slice(0, restants)
    if ([...files].length > restants) {
      setError(`Seules les ${restants} premières ont été retenues (${MAX} au maximum).`)
    }

    setBusy(true)
    try {
      const ajoutees: string[] = []
      for (const file of liste) {
        const refus = refuserFichier(file)
        if (refus) {
          setError(refus)
          continue
        }
        ajoutees.push(await resizeToPortrait(file))
      }
      if (ajoutees.length > 0) onChange([...values, ...ajoutees])
    } catch {
      setError('Cette image n’a pas pu être lue.')
    } finally {
      setBusy(false)
    }
  }

  function onDrop(event: DragEvent): void {
    event.preventDefault()
    setDragging(false)
    if (event.dataTransfer.files?.length) void ajouter(event.dataTransfer.files)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {values.map((src, index) => (
          <Box
            key={src.slice(-24) + index}
            sx={{
              position: 'relative',
              width: VIGNETTE_W + 8,
              height: VIGNETTE_H + 8,
              borderRadius: 2,
              overflow: 'hidden',
              border: index === 0 ? 2 : 1,
              borderColor: index === 0 ? 'primary.main' : 'divider',
            }}
          >
            <Box
              component="img"
              src={src}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />

            {index === 0 && MAX > 1 && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  textAlign: 'center',
                  fontSize: 10,
                  py: '1px',
                }}
              >
                Vignette
              </Typography>
            )}

            <Box
              sx={{
                position: 'absolute',
                inset: 'auto 0 0 0',
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                py: '2px',
                bgcolor: 'rgba(0,0,0,.55)',
              }}
            >
              {index > 0 && (
                <Tooltip title="Mettre en vignette">
                  <IconButton
                    size="small"
                    sx={{ color: '#fff', p: 0.25 }}
                    aria-label={`Mettre la photo ${index + 1} en vignette`}
                    onClick={() => {
                      const suite = values.filter((_, i) => i !== index)
                      onChange([src, ...suite])
                    }}
                  >
                    <StarIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Retirer">
                <IconButton
                  size="small"
                  sx={{ color: '#fff', p: 0.25 }}
                  aria-label={`Retirer la photo ${index + 1}`}
                  onClick={() => onChange(values.filter((_, i) => i !== index))}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}

        {!complet && (
          <Box
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                inputRef.current?.click()
              }
            }}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            aria-label="Ajouter une photo"
            sx={{
              width: VIGNETTE_W + 8,
              height: VIGNETTE_H + 8,
              borderRadius: 2,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              border: '2px dashed',
              borderColor: dragging ? 'primary.main' : 'action.disabled',
              bgcolor: dragging ? 'action.hover' : 'background.default',
              opacity: busy ? 0.6 : 1,
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Box sx={{ textAlign: 'center', px: 1 }}>
              <AddPhotoIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.disabled', lineHeight: 1.3 }}
              >
                {dragging ? 'Déposez ici' : 'Glissez ou cliquez'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', color: 'text.secondary', mt: 1, lineHeight: 1.3 }}
      >
        {legende ?? `${values.length} / ${MAX}`} — réduite{MAX > 1 ? 's' : ''} à {PHOTO_WIDTH}×
        {PHOTO_HEIGHT} sur votre appareil.
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mt: 2, py: 0, px: 1 }}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      <input
        ref={inputRef}
        hidden
        multiple
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => {
          if (e.target.files?.length) void ajouter(e.target.files)
          // Réinitialise, sinon rechoisir LE MÊME fichier ne déclenche rien.
          e.target.value = ''
        }}
      />
    </Box>
  )
}
